import { inject, Service } from '@angular/core';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { LicenseActivationType } from '@portal-core/licenses/enums/license-activation-type.enum';
import { License } from '@portal-core/licenses/models/license.model';
import { LicensesService } from '@portal-core/licenses/services/licenses.service';
import { SitesService } from '@portal-core/sites/services/sites.service';
import { CoreDataService } from '@portal-output/core/services/core-data.service';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

@Service()
export class CoreService {
  private readonly coreDataService = inject(CoreDataService);
  private readonly licensesService = inject(LicensesService);
  private readonly licenseUsersService = inject(LicenseUsersService);
  private readonly authService = inject(AuthService);
  private readonly sitesService = inject(SitesService);

  // User
  getUserId(): string {
    return this.authService.getUserId();
  }

  // LicenseUser
  getLicenseUser(): LicenseUser {
    return this.licenseUsersService.getLicenseUserByUserId(this.getUserId(), this.getLicenseId());
  }

  getLicenseUser$(): Observable<LicenseUser> {
    return this.licenseUsersService.getLicenseUserByUserId$(this.getUserId(), this.getLicenseId());
  }

  // License
  getLicenseId(): number {
    return this.coreDataService.getLicenseId();
  }

  getLicenseId$(): Observable<number> {
    return this.coreDataService.getLicenseId$();
  }

  getLicense$(): Observable<License> {
    return this.licensesService.getItemById$(this.getLicenseId());
  }

  getLicense(): License {
    return this.licensesService.getItemById(this.getLicenseId());
  }

  // Active License
  getActiveLicense(): License {
    const license = this.getLicense();
    return license && license.LicenseActivationStatus === LicenseActivationType.Active ? license : null;
  }

  getActiveLicense$(): Observable<License> {
    return this.getLicense$().pipe(
      map(license => license && license.LicenseActivationStatus === LicenseActivationType.Active ? license : null)
    );
  }

  // Site
  getSiteKey(): string {
    return this.coreDataService.getSiteKey();
  }

  getSiteKey$(): Observable<string> {
    return this.coreDataService.getSiteKey$();
  }

  /** Sets the site key and emits once the site's stylesheet has been injected. */
  setSiteKey$(siteKey: string): Observable<void> {
    const licenseId$: Observable<number> = this.sitesService.isValidSiteKey(siteKey) ? this.sitesService.getLicenseId$(siteKey) : of(null);

    return licenseId$.pipe(
      catchError(() => of(null)),
      switchMap(licenseId => this.coreDataService.setSite$(siteKey, licenseId)),
      switchMap(() => this.sitesService.injectSiteStyles$(siteKey)),
      // Drop injectSiteStyles$'s value rather than just declaring it away. It really does emit
      // something - portal-core's LinkService.inject$ nexts the injected <link> element - so typing
      // this Observable<void> without the map would be a false declaration that nothing checks,
      // since injectSiteStyles$ is typed `any`. The element is an implementation detail of setting
      // the site key and no caller wants it.
      map((): void => undefined)
    );
  }

  // Return Url
  getReturnUrl(): string {
    return this.coreDataService.getReturnUrl();
  }

  setReturnUrl$(returnUrl: string): Observable<void> {
    return this.coreDataService.setReturnUrl$(returnUrl);
  }
}
